import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { translations } from '../data/mockData';

const Cart = () => {
  const { language } = useLanguage();
  const t = translations[language].cart;
  const navigate = useNavigate();
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    getTotal,
  } = useCart();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300"
        onClick={() => setIsCartOpen(false)}
      ></div>

      {/* Cart Sidebar */}
      <div 
        className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-[#141619] shadow-2xl z-50 flex flex-col animate-slide-in-right border-l border-[#2a2d32]"
        data-testid="cart-sidebar"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#2a2d32]">
          <div className="flex items-center space-x-3">
            <ShoppingBag className="w-6 h-6 text-[#D4AF37]" />
            <h2 className="text-2xl font-bold text-white">{t.title}</h2>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-[#1a1d21] rounded-full transition-colors duration-150"
            data-testid="close-cart-btn"
          >
            <X className="w-6 h-6 text-[#9CA3AF] hover:text-white" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="w-16 h-16 text-[#3a3d42] mb-4" />
              <p className="text-lg text-[#9CA3AF]">{t.empty}</p>
              <button
                onClick={() => setIsCartOpen(false)}
                className="mt-6 px-6 py-3 bg-[#D4AF37] text-[#141619] rounded-full font-mono text-xs uppercase tracking-widest hover:bg-[#c9a432] transition-all duration-300"
                data-testid="continue-shopping-btn"
              >
                {t.continueShopping}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item.service_id}
                  className="p-4 bg-[#1a1d21] rounded-xl border border-[#2a2d32] hover:border-[#D4AF37] transition-all duration-300"
                  data-testid={`cart-item-${item.service_id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-1">
                        {item.name || `Service ${item.service_id.substring(0, 8)}`}
                      </h3>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.service_id)}
                      className="ml-2 p-1.5 hover:bg-red-500/20 rounded-lg transition-colors duration-150 group"
                      data-testid={`remove-item-${item.service_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-[#9CA3AF] group-hover:text-red-500" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    {/* Quantity Controls */}
                    <div className="flex items-center space-x-2 bg-[#141619] rounded-full border border-[#2a2d32] px-3 py-1.5">
                      <button
                        onClick={() => updateQuantity(item.service_id, item.quantity - 1)}
                        className="p-1 hover:bg-[#2a2d32] rounded-full transition-colors duration-150"
                      >
                        <Minus className="w-4 h-4 text-[#9CA3AF]" />
                      </button>
                      <span className="w-8 text-center font-medium text-white">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.service_id, item.quantity + 1)}
                        className="p-1 hover:bg-[#2a2d32] rounded-full transition-colors duration-150"
                      >
                        <Plus className="w-4 h-4 text-[#9CA3AF]" />
                      </button>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                      <div className="text-lg font-bold text-[#D4AF37]">
                        €{(item.price * item.quantity).toFixed(2)}
                      </div>
                      {item.recurring && (
                        <div className="text-xs text-[#6B7280]">/month</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-[#2a2d32] p-6 bg-[#1a1d21]">
            <div className="flex justify-between items-center mb-6">
              <span className="text-lg font-semibold text-white">
                {t.total}:
              </span>
              <span className="text-2xl font-bold text-[#D4AF37]">
                €{getTotal().toFixed(2)}
              </span>
            </div>
            <button
              onClick={handleCheckout}
              className="w-full px-8 py-4 bg-[#D4AF37] text-[#141619] rounded-full font-mono text-sm uppercase tracking-widest hover:bg-[#c9a432] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-bold"
              data-testid="checkout-btn"
            >
              {t.checkout}
            </button>
            <button
              onClick={() => setIsCartOpen(false)}
              className="w-full mt-3 px-6 py-3 bg-transparent text-white rounded-full font-mono text-xs uppercase tracking-widest hover:bg-[#2a2d32] transition-all duration-300 border border-[#2a2d32]"
            >
              {t.continueShopping}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;
